import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Volume2 } from "lucide-react";

const ARABIC_LETTERS = [
  { ar: "ا", tr: "Alif", ex: "Asad (lion)" },
  { ar: "ب", tr: "Ba", ex: "Baab (door)" },
  { ar: "ت", tr: "Ta", ex: "Tamr (dates)" },
  { ar: "ث", tr: "Tha", ex: "Thawb (dress)" },
  { ar: "ج", tr: "Jeem", ex: "Jamal (camel)" },
  { ar: "ح", tr: "Ha", ex: "Hoot (whale)" },
  { ar: "خ", tr: "Kha", ex: "Khubz (bread)" },
  { ar: "د", tr: "Dal", ex: "Deek (rooster)" },
];

const DUAS = [
  { title: "Before eating", ar: "بِسْمِ اللَّهِ", tr: "Bismillah", meaning: "In the name of Allah." },
  { title: "After eating", ar: "الْحَمْدُ لِلَّهِ", tr: "Alhamdulillah", meaning: "All praise is for Allah." },
  { title: "When starting", ar: "بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ", tr: "Bismillahi-r-Rahmani-r-Raheem", meaning: "In the name of Allah, the Most Gracious, the Most Merciful." },
  { title: "Saying thanks", ar: "شُكْرًا", tr: "Shukran", meaning: "Thank you." },
];

const COLORS = [
  { en: "Red", ar: "أَحْمَر", tr: "Ahmar", cls: "bg-brand-red text-white" },
  { en: "Yellow", ar: "أَصْفَر", tr: "Asfar", cls: "bg-brand-yellow" },
  { en: "Green", ar: "أَخْضَر", tr: "Akhdar", cls: "bg-brand-green text-white" },
  { en: "Blue", ar: "أَزْرَق", tr: "Azraq", cls: "bg-brand-blue" },
  { en: "White", ar: "أَبْيَض", tr: "Abyad", cls: "bg-white" },
  { en: "Black", ar: "أَسْوَد", tr: "Aswad", cls: "bg-brand-ink text-white" },
];

function speak(text, lang = "ar-SA", rate = 0.75) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  u.rate = rate;
  window.speechSynthesis.speak(u);
}

export default function IslamicGame() {
  const [tab, setTab] = useState("letters");
  const [active, setActive] = useState(null);

  const tabs = [
    { id: "letters", label: "Arabic Letters" },
    { id: "duas", label: "Little Duas" },
    { id: "colors", label: "Colors in Arabic" },
  ];

  return (
    <main className="mx-auto max-w-6xl px-6 py-10" data-testid="islamic-game">
      <Link to="/games" className="inline-flex items-center gap-2 text-brand-ink/80 hover:text-brand-red font-bold">
        <ArrowLeft size={18} /> All games
      </Link>

      <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-ink">Islamic Basics</h1>
          <p className="mt-2 text-brand-ink/80 text-lg">Alif, Ba, Ta… a beautiful start to Arabic learning.</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-white px-4 py-2 shadow-brutal-sm">
          <Volume2 size={18} /> <span className="text-sm font-bold">Sound on</span>
        </div>
      </div>

      <div className="mt-6 inline-flex flex-wrap gap-2 p-1.5 rounded-full border-[3px] border-brand-ink bg-white shadow-brutal-sm">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`islamic-tab-${t.id}`}
            className={`px-4 py-2 rounded-full font-bold text-sm transition-colors ${tab === t.id ? "bg-brand-green text-white" : "text-brand-ink hover:bg-brand-yellow/60"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "letters" && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="islamic-letters-grid">
          {ARABIC_LETTERS.map((l) => (
            <button
              key={l.tr}
              onClick={() => { setActive(l.tr); speak(l.ar, "ar-SA"); setTimeout(() => setActive(null), 800); }}
              data-testid={`arabic-letter-${l.tr}`}
              className={`rounded-3xl border-[3px] border-brand-ink bg-white p-6 shadow-brutal-sm lift-hover flex flex-col items-center ${active === l.tr ? "animate-pop" : ""}`}
            >
              <span className="font-arabic text-7xl text-brand-green leading-none" dir="rtl">{l.ar}</span>
              <span className="mt-3 font-display font-semibold text-2xl">{l.tr}</span>
              <span className="text-sm text-brand-ink/60">{l.ex}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "duas" && (
        <div className="mt-8 grid gap-5 md:grid-cols-2" data-testid="islamic-duas-grid">
          {DUAS.map((d) => (
            <div key={d.title} className="rounded-3xl border-[3px] border-brand-ink bg-white p-6 shadow-brutal-sm">
              <p className="uppercase text-xs font-bold tracking-widest text-brand-green">{d.title}</p>
              <p className="font-arabic text-4xl text-brand-ink mt-3 leading-tight" dir="rtl">{d.ar}</p>
              <p className="mt-2 font-display font-semibold text-xl text-brand-red">{d.tr}</p>
              <p className="text-brand-ink/70 mt-1">{d.meaning}</p>
              <button
                onClick={() => speak(d.ar, "ar-SA", 0.7)}
                data-testid={`dua-play-${d.tr.replace(/[^a-zA-Z]/g, "")}`}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-green text-white font-bold px-4 py-2 btn-brutal text-sm"
              >
                <Volume2 size={16} /> Hear it
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "colors" && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="islamic-colors-grid">
          {COLORS.map((c) => (
            <button
              key={c.en}
              onClick={() => { setActive(c.en); speak(c.ar, "ar-SA"); setTimeout(() => setActive(null), 800); }}
              data-testid={`arabic-color-${c.en.toLowerCase()}`}
              className={`rounded-3xl border-[3px] border-brand-ink ${c.cls} p-6 shadow-brutal-sm lift-hover text-center ${active === c.en ? "animate-pop" : ""}`}
            >
              <p className="font-arabic text-5xl leading-none" dir="rtl">{c.ar}</p>
              <p className="mt-3 font-display font-semibold text-2xl">{c.tr}</p>
              <p className={`text-sm ${c.cls.includes("text-white") ? "text-white/85" : "text-brand-ink/70"}`}>{c.en}</p>
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
